import { QueryClient } from "react-query";
import axios from "axios";
import { nuclearn_user_token } from "src/App";
import { loginRequest } from "src/helpers/AzureAD/azureAD";
import { TAB_ID, TAB_ID_HEADER } from "src/helpers/tabId";
import {
  CSRF_HEADER_NAME,
  getCSRFToken,
  ensureCSRFToken,
  shouldApplyCSRFProtection,
  clearCSRFToken,
} from "src/helpers/csrf";

// Extend Window interface to include msal
declare global {
  interface Window {
    msal?: any;
  }
}

// Extend AxiosRequestConfig to include custom retry tracking
declare module "axios" {
  export interface AxiosRequestConfig {
    _csrfRetryCount?: number;
  }
}

// Add a request interceptor
export const queryClient = new QueryClient();

// CSRF retry configuration
const MAX_CSRF_RETRIES = 2;

// Token refresh utility functions
const refreshAzureAuthNToken = async () => {
  try {
    dispatchAuthStatusEvent("Refreshing Azure authentication token...");

    // Get MSAL instance from window if available
    const msalInstance = window.msal;
    if (!msalInstance) {
      console.error("MSAL instance not available for token refresh");
      dispatchAuthStatusEvent("MSAL instance not available for token refresh");
      return null;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.error("No accounts available for token refresh");
      dispatchAuthStatusEvent("No accounts available for token refresh");
      return null;
    }

    // Attempt silent token refresh
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });

    console.log("Azure AuthN token refreshed successfully");
    dispatchAuthStatusEvent(
      "Azure authentication token refreshed successfully",
    );
    return response.accessToken;
  } catch (error) {
    console.error("Failed to refresh Azure AuthN token:", error);
    dispatchAuthStatusEvent(
      "Failed to refresh Azure authentication token",
      error,
    );
    return null;
  }
};

const refreshNuclearnAuthZToken = async (azureAuthNToken: string) => {
  try {
    dispatchAuthStatusEvent("Refreshing Nuclearn authorization token...");

    // Import the function dynamically to avoid circular dependencies
    const { getNuclearnAuthZToken } =
      await import("src/helpers/AzureAD/azureAD");

    const authZToken = await getNuclearnAuthZToken(azureAuthNToken);
    console.log("Nuclearn AuthZ token refreshed successfully");
    dispatchAuthStatusEvent(
      "Nuclearn authorization token refreshed successfully",
    );

    // Invalidate and refetch the authZToken query
    await queryClient.invalidateQueries({ queryKey: ["authZToken"] });
    await queryClient.refetchQueries({ queryKey: ["authZToken"] });

    return authZToken;
  } catch (error) {
    console.error("Failed to refresh Nuclearn AuthZ token:", error);
    dispatchAuthStatusEvent(
      "Failed to refresh Nuclearn authorization token",
      error,
    );
    return null;
  }
};

const refreshTokensSequentially = async () => {
  console.log("Starting sequential token refresh...");

  // Step 1: Refresh Azure AuthN token first
  const azureAuthNToken = await refreshAzureAuthNToken();
  if (!azureAuthNToken) {
    console.error(
      "AuthN token refresh failed, cannot proceed with AuthZ refresh",
    );
    return false;
  }

  // Step 2: Only refresh AuthZ token if AuthN was successful
  const authZToken = await refreshNuclearnAuthZToken(azureAuthNToken);
  if (!authZToken) {
    console.error("AuthZ token refresh failed");
    return false;
  }

  console.log("Sequential token refresh completed successfully");
  return true;
};

// Custom event for communicating auth status to components
export const dispatchAuthStatusEvent = (status: string, error?: any) => {
  const event = new CustomEvent("authStatusUpdate", {
    detail: { status, error },
  });
  window.dispatchEvent(event);
};

// Check if user is authenticated via Azure AD (has MSAL accounts)
const isAzureADUser = (): boolean => {
  const msalInstance = window.msal;
  return !!(msalInstance?.getAllAccounts?.()?.length > 0);
};

// Clear auth state and redirect to login
const clearAuthAndRedirect = () => {
  // Clear localStorage tokens
  if (nuclearn_user_token) {
    localStorage.removeItem(nuclearn_user_token);
  }
  localStorage.removeItem("nuclearn_user");

  // Clear CSRF token
  clearCSRFToken();

  // Clear React Query cache
  queryClient.clear();

  // Redirect to login if not already on auth pages
  if (
    !window.location.pathname.startsWith("/login") &&
    !window.location.pathname.startsWith("/register")
  ) {
    window.location.href = "/login";
  }
};

export const setupAxiosInterceptors = () => {
  axios.interceptors.request.use(
    async (request) => {
      const token = localStorage.getItem(nuclearn_user_token ?? "");
      if (token) {
        request.headers.Authorization = token;
      }
      // Lets the backend alert the tab that started a piece of work.
      request.headers[TAB_ID_HEADER] = TAB_ID;

      // Add CSRF token to state-changing requests on protected endpoints
      // (auth forms before JWT is available)
      // See: https://github.com/NuclearnAI/frontend/issues/719
      const isStateChangingMethod = ["POST", "PUT", "PATCH", "DELETE"].includes(
        request.method?.toUpperCase() || "",
      );
      if (
        request.url &&
        isStateChangingMethod &&
        shouldApplyCSRFProtection(request.url)
      ) {
        console.log(
          "[CSRF] Protected request detected:",
          request.method,
          request.url,
        );

        // Ensure CSRF token is available (fetches from server if needed)
        // This also ensures the httponly cookie is set by waiting for the fetch to complete
        try {
          const csrfToken = await ensureCSRFToken();
          if (!csrfToken) {
            console.error("[CSRF] Token fetch returned empty token");
          }
          // Use the returned token directly to ensure we have the latest value
          request.headers[CSRF_HEADER_NAME] = csrfToken || getCSRFToken();
          console.log(
            "[CSRF] Added X-CSRF-Token header, length:",
            (csrfToken || getCSRFToken()).length,
          );
        } catch (error) {
          console.error("[CSRF] Failed to fetch token:", error);
          // Still try to use any cached token
          const cachedToken = getCSRFToken();
          request.headers[CSRF_HEADER_NAME] = cachedToken;
          console.log("[CSRF] Using cached token, length:", cachedToken.length);
        }
        // Include credentials (cookies) for cross-origin CSRF-protected requests
        // Required because frontend/backend run on different ports in dev
        request.withCredentials = true;
        console.log("[CSRF] Set withCredentials: true for cookie transmission");
      }

      return request;
    },
    (error) => {
      console.error("Request error:", error);
      dispatchAuthStatusEvent("Request error occurred", error);
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Normalize structured authorization denials (#29834): the backend
      // sends detail as {message, code} on authz 403s. Every axios consumer
      // funnels through here, so replacing the object with its message
      // string fixes every `data.detail` render site at once. The code moves
      // to data.detail_code for programmatic handlers (e.g. Login's
      // EMAIL_NOT_VALIDATED branch). Plain-string details and
      // validation-error arrays pass through untouched.
      const structuredDetail = error.response?.data?.detail;
      if (
        structuredDetail !== null &&
        typeof structuredDetail === "object" &&
        !Array.isArray(structuredDetail) &&
        typeof structuredDetail.message === "string"
      ) {
        if (structuredDetail.code !== undefined) {
          error.response.data.detail_code = structuredDetail.code;
        }
        error.response.data.detail = structuredDetail.message;
      }

      // Handle CSRF errors - retry with fresh token
      // The fastapi-csrf-protect library returns messages like:
      // - "The CSRF signatures submitted do not match" (401)
      // - "CSRF token missing or invalid" (401/403)
      // - "Missing Cookie: `csrf_token`" (400 - MissingTokenError)
      // - "Bad X-CSRF-Token header. Expected value \"<Token>\"" (422 - empty/malformed header)
      const errorDetail = error.response?.data?.detail?.toLowerCase?.() || "";
      const status = error.response?.status;
      const isCSRFError =
        (status === 400 ||
          status === 401 ||
          status === 403 ||
          status === 422) &&
        (errorDetail.includes("csrf") ||
          errorDetail.includes("signatures submitted") ||
          errorDetail.includes("missing cookie"));
      if (isCSRFError) {
        const originalRequest = error.config;
        const retryCount = originalRequest._csrfRetryCount || 0;

        // Retry up to MAX_CSRF_RETRIES times with increasing delays
        if (retryCount < MAX_CSRF_RETRIES) {
          console.log(
            `[CSRF] CSRF error detected (${errorDetail}), attempt ${
              retryCount + 1
            }/${MAX_CSRF_RETRIES}, fetching fresh token...`,
          );
          originalRequest._csrfRetryCount = retryCount + 1;

          try {
            // Add a small delay before retry to allow cookie to be set properly
            // Progressive delay: 500ms, 1000ms, 1500ms, etc.
            // NOTE: limited to 500ms and 1000ms due to MAX_CSRF_RETRIES === 2
            const delayMs = (retryCount + 1) * 500;
            await new Promise((resolve) => setTimeout(resolve, delayMs));

            // Force refresh the CSRF token (clears cache and fetches new)
            // This also ensures the httponly cookie is refreshed via the fetch
            const freshToken = await ensureCSRFToken(true);
            console.log(
              "[CSRF] Got fresh token, length:",
              freshToken?.length || 0,
            );

            // Create a clean retry config to avoid stale header issues
            const retryConfig = {
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                [CSRF_HEADER_NAME]: freshToken,
              },
              withCredentials: true,
            };

            console.log("[CSRF] Retrying request with fresh token");
            return axios(retryConfig);
          } catch (csrfError) {
            console.error(
              "[CSRF] Failed to refresh token for retry:",
              csrfError,
            );
            // Continue to transform the error below
          }
        }

        // All retries exhausted - transform to user-friendly error
        console.error(
          `[CSRF] CSRF retry exhausted after ${MAX_CSRF_RETRIES} attempts`,
        );

        // Replace the cryptic CSRF error with a user-friendly message
        const userFriendlyError = {
          ...error,
          response: {
            ...error.response,
            data: {
              ...error.response.data,
              detail:
                "Your session needs to be refreshed. Please reload the page and try again.",
              _originalDetail: error.response.data.detail,
              _csrfError: true,
            },
          },
        };
        return Promise.reject(userFriendlyError);
      }

      // Handle 524 errors (server timeouts) - don't logout, just report
      // Note: Sentry is configured to drop 524 errors (see sentry.ts beforeSend filter)
      if (
        error.response?.status === 524 &&
        !error.response.config?.url?.endsWith("/auth/login")
      ) {
        console.error("Server timeout (524)");
        dispatchAuthStatusEvent("Server timeout, please try again", error);
        return Promise.reject(error);
      }

      // Handle 401 errors (auth failures) - may need to logout
      if (
        error.response?.status === 401 &&
        !error.response.config.url.endsWith("/auth/login")
      ) {
        // Only attempt Azure AD token refresh if user is an Azure AD user
        if (isAzureADUser()) {
          console.log(
            "Auth token expired, starting sequential token refresh...",
          );
          dispatchAuthStatusEvent("Token expired, refreshing tokens...");

          const refreshSuccess = await refreshTokensSequentially();

          if (refreshSuccess) {
            console.log(
              "Token refresh successful, retrying original request...",
            );
            dispatchAuthStatusEvent(
              "Token refresh successful, retrying request",
            );
            return axios(error.config);
          }
        }

        // For raw credentials users, or if Azure AD refresh failed:
        // Clear auth and redirect to login
        console.log("Session expired, redirecting to login...");
        dispatchAuthStatusEvent("Session expired, please log in again");
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // For other errors, also dispatch the event
      if (error.response?.status >= 400) {
        dispatchAuthStatusEvent(`HTTP ${error.response.status} error`, error);
      }

      return Promise.reject(error);
    },
  );
};
